import { Link, useLocation } from "react-router-dom";
import { 
  Leaf, 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  Heart, 
  Brain, 
  Star, 
  Clock, 
  Sun, 
  Wind, 
  HeartHandshake, 
  Calendar,
  LogOut,
  Menu,
  X,
  Settings,
  Compass,
  Layers,
  Lightbulb,
  Gem,
  Target,
  MessageCircle,
  Award,
  Moon,
  Monitor,
  Globe,
  Bell,
  GraduationCap,
  FileText,
  RefreshCw,
  ClipboardList,
  Shield,
  Search,
  Flame,
  ChevronDown,
  ChevronRight,
  Anchor,
  Eye,
  GitBranch,
  LayoutGrid,
  ListOrdered,
  Mail,
  Folder,
  Focus,
  Timer,
  Flag,
  Video,
  Sparkles,
  ShieldCheck,
  Trophy,
  ListChecks,
  Rocket,
  Radio,
  BatteryCharging,
  Zap,
  BarChart3,
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useState, useEffect } from "react";
import axios from "axios";
import { useTheme } from "../lib/theme";
import { LANGUAGES } from "../lib/languages";
import { useI18n } from "../lib/i18n";
import { TrialBadge } from "./TrialBanner";
import InstallBanner from "./InstallBanner";
import { hardReload } from "../lib/appVersion";
import { API } from "../App";
import { autoSyncCalendly } from "../lib/calendlyAutoSync";

const SidebarSection = ({ label, isOpen, onToggle, links, location, onLinkClick, testId }) => (
  <div data-testid={testId}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
      data-testid={`${testId}-toggle`}
    >
      <span>{label}</span>
      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
    </button>
    {isOpen && (
      <div className="space-y-0.5 mt-0.5">
        {links.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            onClick={onLinkClick}
            className={`
              flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm
              ${location.pathname === link.path 
                ? 'bg-primary text-white' 
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
            `}
            data-testid={`nav-${link.path.split('/').pop()}`}
          >
            <link.icon className="w-4 h-4" />
            {link.label}
          </Link>
        ))}
      </div>
    )}
  </div>
);

const DashboardLayout = ({ children, user, logout, fullBleed = false }) => {
  const [refreshing, setRefreshing] = useState(false);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, cycleTheme } = useTheme();
  const { locale, setLocale, t } = useI18n();
  const [pendingCount, setPendingCount] = useState(0);
  const [therapistCharter, setTherapistCharter] = useState(null);

  useEffect(() => { if (user?.role === "therapist") autoSyncCalendly(); }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role === "therapist") {
      axios.get(`${API}/therapist/patients/pending`, { withCredentials: true })
        .then(res => setPendingCount(res.data.length))
        .catch(() => {});
    }
    if (user?.role === "patient" && user?.therapist_id) {
      axios.get(`${API}/patient/therapist-info`, { withCredentials: true })
        .then(res => setTherapistCharter(res.data))
        .catch(() => {});
    }
  }, [user, location.pathname]);

  const isTherapist = user?.role === "therapist";
  const isAdmin = user?.email === "victor.loher@vmlh-tcc.fr";
  const therapistLinks = [
    { path: "/therapist", icon: LayoutDashboard, label: t("nav.dashboard") },
    { path: "/therapist/patients", icon: Users, label: t("nav.patients"), badge: pendingCount > 0 ? pendingCount : null },
  ];
  const therapistToolLinks = [
    { path: "/therapist/exercises", icon: BookOpen, label: t("nav.exercises") },
    { path: "/therapist/session-reports", icon: ClipboardList, label: t("nav.sessionReports") },
    { path: "/therapist/meditations", icon: Video, label: t("nav.meditations", "Vidéos de méditation") },
  ];
  const therapistDoneLink = { path: "/therapist/my-exercises", icon: FileText, label: t("nav.myDoneExercises") };
  const therapistAdminLinks = [
    { path: "/therapist/statistics", icon: BarChart3, label: t("nav.statistics") },
    ...(isAdmin ? [{ path: "/admin", icon: Settings, label: t("nav.administration") }] : []),
  ];

  const patientLinks = [
    { path: "/patient", icon: LayoutDashboard, label: t("nav.dashboard") },
    { path: "/patient/badges", icon: Trophy, label: t("nav.myBadges", "Mes Badges") },
    { path: "/patient/my-exercises", icon: FileText, label: t("nav.myExercises") },
    { path: "/patient/meditations", icon: Video, label: t("nav.myMeditations", "Mes Meditations") },
    { path: "/patient/ders", icon: ClipboardList, label: t("nav.dersQuestionnaire") },
  ];

  const cbtExerciseLinks = [
    { type: "mood_tracking", path: "/exercises/mood", icon: Heart, label: t("exercise.mood") },
    { type: "emotion_register", path: "/exercises/emotion-register", icon: Brain, label: t("exercise.emotion_register") },
    { type: "gratitude", path: "/exercises/gratitude", icon: Star, label: t("exercise.gratitude") },
    { type: "pomodoro", path: "/exercises/pomodoro", icon: Clock, label: t("exercise.pomodoro") },
    { type: "meditation", path: "/exercises/meditation", icon: Sun, label: t("exercise.meditation") },
    { type: "cognitive_defusion", path: "/exercises/cognitive-defusion", icon: Wind, label: t("exercise.cognitive_defusion") },
    { type: "self_compassion", path: "/exercises/self-compassion", icon: HeartHandshake, label: t("exercise.self_compassion") },
    { type: "behavioral_activation", path: "/exercises/behavioral-activation", icon: Calendar, label: t("exercise.behavioral_activation") },
    { type: "values_exploration", path: "/exercises/values-exploration", icon: Compass, label: t("exercise.values_exploration") },
    { type: "needs_exploration", path: "/exercises/needs-exploration", icon: Layers, label: t("exercise.needs_exploration") },
    { type: "problem_solving", path: "/exercises/problem-solving", icon: Lightbulb, label: t("exercise.problem_solving") },
    { type: "qualities_exploration", path: "/exercises/qualities-exploration", icon: Gem, label: t("exercise.qualities_exploration") },
    { type: "capabilities_exploration", path: "/exercises/capabilities-exploration", icon: Target, label: t("exercise.capabilities_exploration") },
    { type: "assertiveness", path: "/exercises/assertiveness", icon: MessageCircle, label: t("exercise.assertiveness") },
    { type: "self_reinforcement", path: "/exercises/self-reinforcement", icon: Award, label: t("exercise.self_reinforcement") },
    { type: "cognitive_restructuring", path: "/exercises/cognitive-restructuring", icon: RefreshCw, label: t("exercise.cognitive_restructuring") },
  ];

  const barkleyParentLinks = [
    { type: "barkley_identity_card", path: "/exercises/barkley/barkley_identity_card", icon: ClipboardList, label: t("barkley.identityCard.title", "Carte d'identité TDAH") },
    { type: "barkley_special_moment", path: "/exercises/barkley/barkley_special_moment", icon: Heart, label: t("barkley.specialMoment.title", "Moment Spécial") },
    { type: "barkley_effective_instructions", path: "/exercises/barkley/barkley_effective_instructions", icon: MessageCircle, label: t("barkley.instructions.title", "Consignes Efficaces") },
    { type: "barkley_token_system", path: "/exercises/barkley/barkley_token_system", icon: Award, label: t("barkley.tokenSystem.title", "Système de Points") },
    { type: "barkley_crisis_plan", path: "/exercises/barkley/barkley_crisis_plan", icon: Shield, label: t("barkley.crisisPlan.title", "Plan Gestion de Crise") },
    { type: "barkley_abc_analysis", path: "/exercises/barkley/barkley_abc_analysis", icon: Search, label: t("barkley.abcAnalysis.title", "Analyse ABC") },
  ];

  const barkleyChildLinks = [
    { type: "barkley_child_volcano", path: "/exercises/barkley/barkley_child_volcano", icon: Flame, label: t("barkley.childVolcano.title", "Mon Volcan Intérieur") },
    { type: "barkley_child_autonomy_timer", path: "/exercises/barkley/barkley_child_autonomy_timer", icon: Clock, label: t("barkley.childAutonomy.title", "Timer d'Autonomie") },
    { type: "barkley_child_superpowers", path: "/exercises/barkley/barkley_child_superpowers", icon: Star, label: t("barkley.childSuperpowers.title", "Mes Super Pouvoirs") },
    { type: "barkley_child_solution_wheel", path: "/exercises/barkley/barkley_child_solution_wheel", icon: Target, label: t("barkley.childSolutions.title", "Roue des Solutions") },
    { type: "barkley_child_success_board", path: "/exercises/barkley/barkley_child_success_board", icon: Star, label: t("barkley.childSuccess.title", "Tableau de Réussites") },
    { type: "barkley_child_instruction_detective", path: "/exercises/barkley/barkley_child_instruction_detective", icon: Search, label: t("barkley.childDetective.title", "Détective Consignes") },
  ];

  const tdahKidsWorkbookLinks = [
    { type: "parent_task_board", path: "/exercises/parent-task-board", icon: ClipboardList, label: t("exercise.parentTaskBoard.title", "Tableau de Missions") },
    { type: "tdah_kids_superpowers", path: "/exercises/tdah-kids/tdah_kids_superpowers", icon: Rocket, label: t("exercise.tdahKids.superpowers.title", "Super-Pouvoirs Cosmiques") },
    { type: "tdah_kids_safety_bubble", path: "/exercises/tdah-kids/tdah_kids_safety_bubble", icon: Shield, label: t("exercise.tdahKids.safetyBubble.title", "Bulle Spatiale") },
    { type: "tdah_kids_relaxation", path: "/exercises/tdah-kids/tdah_kids_relaxation", icon: Moon, label: t("exercise.tdahKids.relaxation.title", "Retour en Orbite") },
    { type: "tdah_kids_agitation_radar", path: "/exercises/tdah-kids/tdah_kids_agitation_radar", icon: Radio, label: t("exercise.tdahKids.radar.title", "Radar Galactique") },
    { type: "tdah_kids_recharge", path: "/exercises/tdah-kids/tdah_kids_recharge", icon: BatteryCharging, label: t("exercise.tdahKids.recharge.title", "Station de Recharge") },
    { type: "tdah_kids_turbo_brain", path: "/exercises/tdah-kids/tdah_kids_turbo_brain", icon: Zap, label: t("exercise.tdahKids.turbo.title", "Vaisseau Turbo") },
    { type: "tdah_kids_mission", path: "/exercises/tdah-kids/tdah_kids_mission", icon: Target, label: t("exercise.tdahKids.mission.title", "Mission Spatiale") },
    { type: "tdah_kids_superhero_day", path: "/exercises/tdah-kids/tdah_kids_superhero_day", icon: Sun, label: t("exercise.tdahKids.superheroDay.title", "Journée d'Astronaute") },
    { type: "tdah_kids_victories", path: "/exercises/tdah-kids/tdah_kids_victories", icon: Star, label: t("exercise.tdahKids.victories.title", "Étoiles de Victoire") },
    { type: "tdah_kids_therapist_questions", path: "/exercises/tdah-kids/tdah_kids_therapist_questions", icon: MessageCircle, label: t("exercise.tdahKids.questions.title", "Message Centre Contrôle") },
  ];

  const barkleyTeenLinks = [
    { type: "barkley_teen_situation_analysis", path: "/exercises/barkley/barkley_teen_situation_analysis", icon: Brain, label: t("barkley.teenSituation.title", "Analyse Situation") },
    { type: "barkley_teen_weekly_planner", path: "/exercises/barkley/barkley_teen_weekly_planner", icon: Calendar, label: t("barkley.teenPlanner.title", "Planning Semaine") },
    { type: "barkley_teen_emotion_journal", path: "/exercises/barkley/barkley_teen_emotion_journal", icon: Heart, label: t("barkley.teenEmotionJournal.title", "Journal Émotions") },
    { type: "barkley_teen_smart_goals", path: "/exercises/barkley/barkley_teen_smart_goals", icon: Target, label: t("barkley.teenSmartGoals.title", "Objectifs SMART") },
    { type: "barkley_teen_cnv", path: "/exercises/barkley/barkley_teen_cnv", icon: MessageCircle, label: t("barkley.teenCNV.title", "Communication NV") },
    { type: "barkley_teen_weekly_review", path: "/exercises/barkley/barkley_teen_weekly_review", icon: ClipboardList, label: t("barkley.teenWeeklyReview.title", "Bilan Hebdo") },
    { type: "ado_school_stress_journal", path: "/exercises/barkley/ado_school_stress_journal", icon: BookOpen, label: t("ado.schoolStress.title", "Journal de stress scolaire") },
    { type: "ado_exposure_plan", path: "/exercises/barkley/ado_exposure_plan", icon: Flag, label: t("ado.exposurePlan.title", "Plan des défis en classe") },
  ];

  const psychoedLinks = [
    { type: "psychoed_tdah_child", path: "/exercises/psychoed/psychoed_tdah_child", icon: Sparkles, label: t("psychoed.tdahChild.title", "Comprendre mon TDAH (Enfant)") },
    { type: "psychoed_limits_child", path: "/exercises/psychoed/psychoed_limits_child", icon: Shield, label: t("psychoed.limitsChild.title", "Mon Jardin Secret - Les Limites (Enfant)") },
    { type: "psychoed_limits_adult", path: "/exercises/psychoed/psychoed_limits_adult", icon: ShieldCheck, label: t("psychoed.limitsAdult.title", "Communiquer ses Limites (Adulte)") },
    { type: "psychoed_schema_therapy", path: "/exercises/psychoed/psychoed_schema_therapy", icon: BookOpen, label: t("psychoed.schemaTherapy.title", "Therapie des Schemas") },
    { type: "psychoed_tcd", path: "/exercises/psychoed/psychoed_tcd", icon: BookOpen, label: t("psychoed.tcd.title", "TCD (Linehan)") },
    { type: "psychoed_dissociation_trauma", path: "/exercises/psychoed/psychoed_dissociation_trauma", icon: BookOpen, label: t("psychoed.dissociationTrauma.title", "Dissociation et Trauma") },
  ];

  const exercicesAvancesLinks = [
    { type: "schema_exercises", path: "/exercises/schema-therapy", icon: ClipboardList, label: t("exercices.schemaTherapy.title", "Exercices Schemas"), groupTypes: ["schema_journal", "memo_flash", "inner_dialogue", "vulnerable_child_letter", "imagery_reparenting"] },
    { type: "tcd_exercises", path: "/exercises/tcd-exercises", icon: ClipboardList, label: t("exercices.tcd.title", "Exercices TCD"), groupTypes: ["chain_analysis", "stop_tip", "radical_acceptance", "check_facts", "dear_man", "give", "fast"] },
    { type: "dissociation_exercises", path: "/exercises/dissociation-exercises", icon: ClipboardList, label: t("exercices.dissociationTrauma.title", "Exercices Dissociation"), groupTypes: ["grounding_54321", "safe_place", "parts_work", "window_tolerance"] },
  ];

  const sexualityLinks = [
    { type: "sexuality_exercises", path: "/exercises/sexuality-exercises", icon: Heart, label: t("nav.sexualityTcc"), groupTypes: ["sex_sensory_mapping", "sex_neutral_scan", "sex_stop_rule", "sex_touch_pause", "sex_sensory_channels", "sex_pelvic_movement", "sex_performance_thoughts", "sex_vitality_journal", "sex_couple_visual_contact", "sex_couple_nonsexual_touch", "sex_couple_sensate_focus", "sex_sensate_journal"] },
  ];

  const actLinks = [
    { type: "act_dropping_anchor", path: "/exercises/act/act_dropping_anchor", icon: Anchor, label: t("act.droppingAnchor.title", "Jeter l'Ancre") },
    { type: "act_expansion", path: "/exercises/act/act_expansion", icon: Heart, label: t("act.expansion.title", "Faire de la Place") },
    { type: "act_observer_self", path: "/exercises/act/act_observer_self", icon: Eye, label: t("act.observerSelf.title", "Le Soi Observateur") },
    { type: "act_choice_point", path: "/exercises/act/act_choice_point", icon: GitBranch, label: t("act.choicePoint.title", "Le Point de Choix") },
    { type: "act_matrix", path: "/exercises/act/act_matrix", icon: LayoutGrid, label: t("act.matrix.title", "La Matrice ACT") },
    { type: "act_committed_action", path: "/exercises/act/act_committed_action", icon: Target, label: t("act.committedAction.title", "Action Engagée") },
    { type: "act_reality_witness", path: "/exercises/act/act_reality_witness", icon: Eye, label: t("act.realityWitness.title", "Soyez témoin de la réalité") },
    { type: "act_choice_question", path: "/exercises/act/act_choice_question", icon: GitBranch, label: t("act.choiceQuestion.title", "La question du choix") },
    { type: "act_control_myth", path: "/exercises/act/act_control_myth", icon: Shield, label: t("act.controlMyth.title", "Le mythe du contrôle") },
  ];

  const perfectionismLinks = [
    { type: "perf_journal", path: "/exercises/barkley/perf_journal", icon: BookOpen, label: t("perf.journal.title", "Journal du perfectionnisme") },
    { type: "perf_good_enough", path: "/exercises/barkley/perf_good_enough", icon: Target, label: t("perf.goodEnough.title", "Expérience « suffisamment bon »") },
    { type: "perf_cost_benefit", path: "/exercises/barkley/perf_cost_benefit", icon: ClipboardList, label: t("perf.costBenefit.title", "Analyse coûts-bénéfices") },
    { type: "perf_standards_continuum", path: "/exercises/barkley/perf_standards_continuum", icon: Flag, label: t("perf.continuum.title", "Continuum des standards") },
    { type: "perf_scale", path: "/exercises/perfectionism-scale", icon: ClipboardList, label: t("perfScale.navTitle", "Questionnaire perfectionnisme"), groupTypes: ["perf_journal", "perf_good_enough", "perf_cost_benefit", "perf_standards_continuum"] },
  ];

  const griefLinks = [
    { type: "grief_memory_journal", path: "/exercises/barkley/grief_memory_journal", icon: BookOpen, label: t("grief.memoryJournal.title", "Journal des souvenirs") },
    { type: "grief_letter", path: "/exercises/barkley/grief_letter", icon: Mail, label: t("grief.letter.title", "Lettre à la personne disparue") },
    { type: "grief_avoidance_hierarchy", path: "/exercises/barkley/grief_avoidance_hierarchy", icon: ListOrdered, label: t("grief.avoidance.title", "Hiérarchie des situations évitées") },
    { type: "grief_reengagement_plan", path: "/exercises/barkley/grief_reengagement_plan", icon: Flag, label: t("grief.reengagement.title", "Plan de réengagement") },
    { type: "grief_scale", path: "/exercises/grief-scale", icon: ClipboardList, label: t("griefScale.navTitle", "Échelle de deuil"), groupTypes: ["grief_memory_journal", "grief_letter", "grief_avoidance_hierarchy", "grief_reengagement_plan"] },
  ];

  const tdahAdulteLinks = [
    { type: "morning_checklist", path: "/exercises/morning-checklist", icon: ListChecks, label: t("checklist.title") },
    { type: "tdah_priorities_abc", path: "/exercises/tdah/tdah_priorities_abc", icon: ListOrdered, label: t("tdah.priorities.title", "Priorités ABC") },
    { type: "tdah_document_management", path: "/exercises/tdah/tdah_document_management", icon: Folder, label: t("tdah.documents.title", "Gestion Documents") },
    { type: "tdah_attention_capacity", path: "/exercises/tdah/tdah_attention_capacity", icon: Focus, label: t("tdah.attention.title", "Capacité Attention") },
    { type: "tdah_automatic_thoughts", path: "/exercises/tdah/tdah_automatic_thoughts", icon: Brain, label: t("tdah.thoughts.title", "Pensées TDAH") },
    { type: "tdah_anti_procrastination", path: "/exercises/tdah/tdah_anti_procrastination", icon: Timer, label: t("tdah.procrastination.title", "Anti-Procrastination") },
    { type: "tdah_goals_motivation", path: "/exercises/tdah/tdah_goals_motivation", icon: Flag, label: t("tdah.goals.title", "Objectifs & Motivation") },
  ];

  const [assignedTypes, setAssignedTypes] = useState(null);
  const [assignedModules, setAssignedModules] = useState(null);
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = localStorage.getItem("sidebar_sections");
      if (saved) return JSON.parse(saved);
    } catch {}
    return { cbt: false, barkleyParent: false, barkleyChild: false, barkleyTeen: false, act: false, tdahAdulte: false, psychoed: false, tdahKidsWorkbook: false, exercicesAvances: false, sexualite: false, selfPractice: false, perfectionism: false, grief: false };
  });

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem("sidebar_sections", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    if (user?.role === "patient") {
      axios.get(`${API}/exercises/assigned-types`, { withCredentials: true })
        .then(res => setAssignedTypes(res.data))
        .catch(() => setAssignedTypes([]));
      axios.get(`${API}/psychoeducation/assignments`, { withCredentials: true })
        .then(res => setAssignedModules(res.data.map(a => a.module_id)))
        .catch(() => setAssignedModules([]));
    }
  }, [user]);

  const filterLinks = (links) => {
    if (user?.role === "patient" && assignedTypes !== null) {
      return links.filter(link => {
        if (link.groupTypes) {
          return link.groupTypes.some(t => assignedTypes.includes(t));
        }
        return assignedTypes.includes(link.type);
      });
    }
    return links;
  };

  const visibleCbt = filterLinks(cbtExerciseLinks);
  const visibleBarkleyParent = filterLinks(barkleyParentLinks);
  const visibleBarkleyChild = filterLinks(barkleyChildLinks);
  const visibleBarkleyTeen = filterLinks(barkleyTeenLinks);
  const visibleAct = filterLinks(actLinks);
  const visibleTdahAdulte = filterLinks(tdahAdulteLinks);
  const perfModuleAssigned = user?.role === "patient" && (assignedModules || []).some((m) => m.startsWith("psychoed_perfectionism"));
  const visiblePerfectionism = perfModuleAssigned && filterLinks(perfectionismLinks).length === 0 ? perfectionismLinks.filter((l) => l.type === "perf_scale") : filterLinks(perfectionismLinks);
  const griefModuleAssigned = user?.role === "patient" && (assignedModules || []).some((m) => m.startsWith("psychoed_grief"));
  const visibleGrief = griefModuleAssigned && filterLinks(griefLinks).length === 0 ? griefLinks.filter((l) => l.type === "grief_scale") : filterLinks(griefLinks);
  const visibleExercicesAvances = filterLinks(exercicesAvancesLinks);
  const visibleSexuality = filterLinks(sexualityLinks);
  const visibleTdahKidsWorkbook = filterLinks(tdahKidsWorkbookLinks);

  const hasAnyExercises = visibleCbt.length > 0 || visiblePerfectionism.length > 0 || visibleGrief.length > 0 || visibleBarkleyParent.length > 0 || visibleBarkleyChild.length > 0 || visibleBarkleyTeen.length > 0 || visibleAct.length > 0 || visibleTdahAdulte.length > 0 || visibleTdahKidsWorkbook.length > 0 || visibleExercicesAvances.length > 0 || visibleSexuality.length > 0;
  const showPsychoeducation = user?.role === "therapist" || (assignedModules !== null && assignedModules.length > 0);

  const mainLinks = user?.role === "therapist" ? therapistLinks : patientLinks;

  const NavLink = ({ link }) => (
    <Link
      to={link.path}
      onClick={() => setSidebarOpen(false)}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${location.pathname === link.path ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
      data-testid={`nav-${link.path.split("/").pop()}`}
    >
      <link.icon className="w-4 h-4" />
      <span className="flex-1">{link.label}</span>
      {link.badge && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{link.badge}</span>}
    </Link>
  );

  const homeLink = user?.role === "patient" ? "/patient" : "/therapist";

  const handleLogout = async () => {
    const isPatient = user?.role === "patient";
    await logout();
    window.location.href = isPatient ? "/login?from=patient" : "/login";
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-card rounded-lg shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 sm:w-72 bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--sidebar-border))] transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 sm:p-6 border-b border-[hsl(var(--sidebar-border))]">
            <Link to={homeLink} className="flex items-center gap-3">
              <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-10 sm:h-12 w-auto" />
              <span className="font-heading text-base sm:text-lg font-semibold">Kintsugi TCC</span>
            </Link>
          </div>

          {/* User info */}
          <div className="p-4 border-b border-[hsl(var(--sidebar-border))]">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-white">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-medium truncate">{user?.name}</p>
                  {user?.role === "therapist" && user?.charter_accepted && (
                    <Link to="/charter" className="flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity" title={t("charter.badgeTooltip")} data-testid="charter-badge-self">
                      <Shield className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </Link>
                  )}
                </div>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role === "therapist" ? t("auth.therapist") : t("auth.patient")}
                </p>
              </div>
            </div>
            {/* Show therapist charter badge for patients */}
            {user?.role === "patient" && therapistCharter?.name && (
              <div className="mt-3 px-2 py-2 rounded-lg bg-muted/50" data-testid="therapist-charter-info">
                <p className="text-xs text-muted-foreground mb-1">{t("charter.myTherapist", "Mon thérapeute")}</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium truncate">{therapistCharter.name}</span>
                  {therapistCharter.charter_accepted && (
                    <Link to="/charter?from=patient" className="flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity" title={t("charter.badgeTooltip")} data-testid="charter-badge-therapist">
                      <Shield className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Charter acceptance banner for existing therapists */}
          {user?.role === "therapist" && user?.charter_accepted === false && (
            <div className="mx-4 mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" data-testid="charter-accept-banner">
              <div className="flex items-start gap-2 mb-2">
                <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                  {t("charter.bannerText", "Veuillez accepter la Charte du Secret Professionnel pour afficher le badge de confiance à vos patients.")}
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/auth/accept-charter`, {}, { withCredentials: true });
                    window.location.reload();
                  } catch (e) { console.error(e); }
                }}
                className="w-full text-xs font-medium px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                data-testid="accept-charter-btn"
              >
                <Shield className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                {t("charter.acceptBtn", "Accepter la charte")}
              </button>
              <a href="/charter" target="_blank" className="block text-center text-xs text-amber-700 dark:text-amber-400 underline mt-1.5">
                {t("charter.readCharter", "Lire la charte complète")}
              </a>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-1">
              {mainLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${location.pathname === link.path 
                      ? 'bg-primary text-white' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                  `}
                  data-testid={`nav-${link.path.split('/').pop()}`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                  {link.badge && (
                    <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="pending-patients-badge">
                      {link.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {/* Exercise sections - grouped by program */}
            {hasAnyExercises && !isTherapist && (
              <div className="mt-6 space-y-3">
                {/* CBT Exercises */}
                {visibleCbt.length > 0 && (
                  <SidebarSection
                    label={t("nav.cbtExercises")}
                    isOpen={openSections.cbt}
                    onToggle={() => toggleSection("cbt")}
                    links={visibleCbt}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-cbt"
                  />
                )}

                {/* Barkley Parents */}
                {visibleBarkleyParent.length > 0 && (
                  <SidebarSection
                    label={t("nav.barkleyParent", "TDAH Enfant - Guide Parental")}
                    isOpen={openSections.barkleyParent}
                    onToggle={() => toggleSection("barkleyParent")}
                    links={visibleBarkleyParent}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-barkley-parents"
                  />
                )}

                {/* Barkley Children */}
                {visibleBarkleyChild.length > 0 && (
                  <SidebarSection
                    label={t("nav.barkleyChild", "TDAH Enfant - Exercices (6-12 ans)")}
                    isOpen={openSections.barkleyChild}
                    onToggle={() => toggleSection("barkleyChild")}
                    links={visibleBarkleyChild}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-barkley-children"
                  />
                )}

                {/* Barkley Teens */}
                {visibleBarkleyTeen.length > 0 && (
                  <SidebarSection
                    label={t("nav.barkleyTeen", "Exercices Ados (12-17 ans)")}
                    isOpen={openSections.barkleyTeen}
                    onToggle={() => toggleSection("barkleyTeen")}
                    links={visibleBarkleyTeen}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-barkley-teens"
                  />
                )}

                {/* Carnet Galactique TDAH Enfant */}
                {visibleTdahKidsWorkbook.length > 0 && (
                  <SidebarSection
                    label={t("nav.tdahKidsWorkbook", "Carnet Galactique TDAH")}
                    isOpen={openSections.tdahKidsWorkbook}
                    onToggle={() => toggleSection("tdahKidsWorkbook")}
                    links={visibleTdahKidsWorkbook}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-tdah-kids-workbook"
                  />
                )}

                {/* L'ACT (Russ Harris) */}
                {visibleAct.length > 0 && (
                  <SidebarSection
                    label={t("nav.actExercises", "L'ACT")}
                    isOpen={openSections.act}
                    onToggle={() => toggleSection("act")}
                    links={visibleAct}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-act"
                  />
                )}

                {/* TDAH Adulte */}
                {visibleTdahAdulte.length > 0 && (
                  <SidebarSection
                    label={t("nav.tdahAdulte", "TDAH Adulte")}
                    isOpen={openSections.tdahAdulte}
                    onToggle={() => toggleSection("tdahAdulte")}
                    links={visibleTdahAdulte}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-tdah-adulte"
                  />
                )}

                {/* Psychoeducation section removed - unified into /psychoeducation page */}

                {/* Exercices Schemas / TCD / Dissociation */}
                {visibleExercicesAvances.length > 0 && (
                  <SidebarSection
                    label={t("nav.exercicesAvances", "Exercices Avances")}
                    isOpen={openSections.exercicesAvances}
                    onToggle={() => toggleSection("exercicesAvances")}
                    links={visibleExercicesAvances}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-exercices-avances"
                  />
                )}

                {/* TCC Sexualite */}
                {visibleSexuality.length > 0 && (
                  <SidebarSection
                    label={t("nav.sexualityTcc")}
                    isOpen={openSections.sexualite}
                    onToggle={() => toggleSection("sexualite")}
                    links={visibleSexuality}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-sexuality"
                  />
                )}
                {/* Deuil */}
                {visibleGrief.length > 0 && (
                  <SidebarSection
                    label={t("nav.grief", "Deuil")}
                    isOpen={openSections.grief}
                    onToggle={() => toggleSection("grief")}
                    links={visibleGrief}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-grief"
                  />
                )}
                {/* Perfectionnisme */}
                {visiblePerfectionism.length > 0 && (
                  <SidebarSection
                    label={t("nav.perfectionism", "Perfectionnisme et excellencisme")}
                    isOpen={openSections.perfectionism}
                    onToggle={() => toggleSection("perfectionism")}
                    links={visiblePerfectionism}
                    location={location}
                    onLinkClick={() => setSidebarOpen(false)}
                    testId="section-perfectionism"
                  />
                )}
                            </div>
            )}

            {/* Thérapeute : OUTILS */}
            {isTherapist && (
              <div className="mt-6" data-testid="sidebar-tools">
                <p className="px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Outils</p>
                <div className="space-y-0.5">
                  {therapistToolLinks.map((link) => <NavLink key={link.path} link={link} />)}
                  <div data-testid="section-self-practice">
                    <button
                      onClick={() => toggleSection("selfPractice")}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${location.pathname.startsWith("/exercises/") ? "text-foreground font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                      data-testid="section-self-practice-toggle"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span className="flex-1 text-left">Autopratique</span>
                      {openSections.selfPractice ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                    {openSections.selfPractice && (
                      <div className="ml-3 pl-2 border-l border-[hsl(var(--sidebar-border))] space-y-2 mt-1" data-testid="self-practice-sections">
                        {/* CBT Exercises */}
                        {visibleCbt.length > 0 && (
                          <SidebarSection
                            label={t("nav.cbtExercises")}
                            isOpen={openSections.cbt}
                            onToggle={() => toggleSection("cbt")}
                            links={visibleCbt}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-cbt"
                          />
                        )}

                        {/* Barkley Parents */}
                        {visibleBarkleyParent.length > 0 && (
                          <SidebarSection
                            label={t("nav.barkleyParent", "TDAH Enfant - Guide Parental")}
                            isOpen={openSections.barkleyParent}
                            onToggle={() => toggleSection("barkleyParent")}
                            links={visibleBarkleyParent}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-barkley-parents"
                          />
                        )}

                        {/* Barkley Children */}
                        {visibleBarkleyChild.length > 0 && (
                          <SidebarSection
                            label={t("nav.barkleyChild", "TDAH Enfant - Exercices (6-12 ans)")}
                            isOpen={openSections.barkleyChild}
                            onToggle={() => toggleSection("barkleyChild")}
                            links={visibleBarkleyChild}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-barkley-children"
                          />
                        )}

                        {/* Barkley Teens */}
                        {visibleBarkleyTeen.length > 0 && (
                          <SidebarSection
                            label={t("nav.barkleyTeen", "Exercices Ados (12-17 ans)")}
                            isOpen={openSections.barkleyTeen}
                            onToggle={() => toggleSection("barkleyTeen")}
                            links={visibleBarkleyTeen}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-barkley-teens"
                          />
                        )}

                        {/* Carnet Galactique TDAH Enfant */}
                        {visibleTdahKidsWorkbook.length > 0 && (
                          <SidebarSection
                            label={t("nav.tdahKidsWorkbook", "Carnet Galactique TDAH")}
                            isOpen={openSections.tdahKidsWorkbook}
                            onToggle={() => toggleSection("tdahKidsWorkbook")}
                            links={visibleTdahKidsWorkbook}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-tdah-kids-workbook"
                          />
                        )}

                        {/* L'ACT (Russ Harris) */}
                        {visibleAct.length > 0 && (
                          <SidebarSection
                            label={t("nav.actExercises", "L'ACT")}
                            isOpen={openSections.act}
                            onToggle={() => toggleSection("act")}
                            links={visibleAct}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-act"
                          />
                        )}

                        {/* TDAH Adulte */}
                        {visibleTdahAdulte.length > 0 && (
                          <SidebarSection
                            label={t("nav.tdahAdulte", "TDAH Adulte")}
                            isOpen={openSections.tdahAdulte}
                            onToggle={() => toggleSection("tdahAdulte")}
                            links={visibleTdahAdulte}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-tdah-adulte"
                          />
                        )}

                        {/* Psychoeducation section removed - unified into /psychoeducation page */}

                        {/* Exercices Schemas / TCD / Dissociation */}
                        {visibleExercicesAvances.length > 0 && (
                          <SidebarSection
                            label={t("nav.exercicesAvances", "Exercices Avances")}
                            isOpen={openSections.exercicesAvances}
                            onToggle={() => toggleSection("exercicesAvances")}
                            links={visibleExercicesAvances}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-exercices-avances"
                          />
                        )}

                        {/* TCC Sexualite */}
                        {visibleSexuality.length > 0 && (
                          <SidebarSection
                            label={t("nav.sexualityTcc")}
                            isOpen={openSections.sexualite}
                            onToggle={() => toggleSection("sexualite")}
                            links={visibleSexuality}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-sexuality"
                          />
                        )}
                        {/* Deuil */}
                        {visibleGrief.length > 0 && (
                          <SidebarSection
                            label={t("nav.grief", "Deuil")}
                            isOpen={openSections.grief}
                            onToggle={() => toggleSection("grief")}
                            links={visibleGrief}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-grief"
                          />
                        )}
                        {/* Perfectionnisme */}
                        {visiblePerfectionism.length > 0 && (
                          <SidebarSection
                            label={t("nav.perfectionism", "Perfectionnisme et excellencisme")}
                            isOpen={openSections.perfectionism}
                            onToggle={() => toggleSection("perfectionism")}
                            links={visiblePerfectionism}
                            location={location}
                            onLinkClick={() => setSidebarOpen(false)}
                            testId="section-perfectionism"
                          />
                        )}
                                    </div>
                    )}
                  </div>
                  <NavLink link={therapistDoneLink} />
                </div>
              </div>
            )}

            {/* No exercises message for patients */}
            {user?.role === "patient" && !hasAnyExercises && assignedTypes !== null && (
              <div className="mt-6 px-4">
                <p className="text-xs text-muted-foreground italic">
                  {t("patient.noExercisesAssigned", "Aucun exercice assigné")}
                </p>
              </div>
            )}

            {/* Psychoeducation */}
            {showPsychoeducation && (
            <div className="mt-8">
              <p className="px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                {t("nav.psychoeducation")}
              </p>
              <div className="space-y-1">
                <Link
                  to="/psychoeducation"
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm
                    ${location.pathname === "/psychoeducation" 
                      ? 'bg-primary text-white' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                  `}
                  data-testid="nav-psychoeducation"
                >
                  <GraduationCap className="w-4 h-4" />
                  {t("nav.theoryModules")}
                </Link>
              </div>
            </div>
            )}

            {/* Thérapeute : ADMINISTRATION */}
            {isTherapist && (
              <div className="mt-8" data-testid="sidebar-admin">
                <p className="px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Administration</p>
                <div className="space-y-0.5">
                  {therapistAdminLinks.map((link) => <NavLink key={link.path} link={link} />)}
                </div>
              </div>
            )}
          </nav>

          {/* Trial Badge */}
          {user?.role === "therapist" && <TrialBadge />}

          {/* Theme & Language toggles */}
          <div className="px-4 py-2 border-t border-[hsl(var(--sidebar-border))]">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={cycleTheme}
                className="flex-1 justify-start gap-2 text-muted-foreground hover:text-foreground"
                data-testid="theme-toggle-btn"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : theme === "light" ? <Moon className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
                {theme === "dark" ? t("profile.lightMode") : theme === "light" ? t("profile.darkMode") : t("profile.autoMode")}
              </Button>
              <label className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm" title={t("profile.language")}>
                <Globe className="w-4 h-4" />
                <select
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  className="h-8 rounded-md border border-transparent bg-transparent px-1 text-xs uppercase font-medium cursor-pointer hover:border-border focus:outline-none"
                  data-testid="language-toggle-btn"
                >
                  {LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.short}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Profile & Logout */}
          <div className="p-4 border-t border-[hsl(var(--sidebar-border))] space-y-1">
            <Link to="/profile">
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
                data-testid="profile-btn"
              >
                <Settings className="w-4 h-4" />
                {t("nav.profile")}
              </Button>
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
              onClick={() => { setRefreshing(true); hardReload(); }}
              title={t("app.refresh_hint")}
              data-testid="refresh-app-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {t("app.refresh")}
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t("auth.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 min-h-screen lg:ml-0 flex flex-col">
        <div className={`flex-1 ${fullBleed ? "pt-14 lg:pt-0" : "px-4 pt-14 pb-4 sm:px-5 lg:px-8 lg:pt-8 overflow-x-hidden"}`}>
          {children}
        </div>
        <InstallBanner variant={user?.role || "default"} />
      </main>
    </div>
  );
};

export default DashboardLayout;
